import { useState } from 'react';
import type { InvoiceLineItem } from '@acitra/shared';
import { calculateWithRoundtrip, roundToPrecision } from '@acitra/shared';
import { EditableValue } from '../../../components/EditableValue';
import { ValidationIndicator } from './ValidationIndicator';

interface LineItemsSectionProps {
  lineItems: InvoiceLineItem[];
  originalLineItems?: InvoiceLineItem[];
  /** Decimal precision for quantity and unit price (from tenant settings) */
  precision: number;
  formatQuantity: (qty: number | undefined) => string;
  formatUnitPrice: (price: number | undefined) => string;
  formatAmount: (amount: number | undefined) => string;
  onLineItemEdit: (
    idx: number,
    field: keyof InvoiceLineItem,
    value: string | number | null
  ) => Promise<void>;
  onLineItemDelete: (idx: number) => Promise<void>;
  onLineItemAdd: () => Promise<void>;
}

function calculateLineItemAmount(item: { quantity?: number; unitPrice?: number }): number | null {
  // Treat null/undefined as 0
  const qty = item.quantity ?? 0;
  const price = item.unitPrice ?? 0;
  return qty * price;
}

function amountsMatch(item: {
  quantity?: number;
  unitPrice?: number;
  amount?: number;
}): boolean | null {
  const { quantity, unitPrice, amount } = item;

  // Treat null/undefined as 0 for validation purposes
  const qtyVal = quantity ?? 0;
  const priceVal = unitPrice ?? 0;
  const amountVal = amount ?? 0;

  const hasQty = qtyVal !== 0;
  const hasPrice = priceVal !== 0;
  const hasAmount = amountVal !== 0;
  const anyNonZero = hasQty || hasPrice || hasAmount;

  // If no fields have non-zero values, validation is indeterminate
  if (!anyNonZero) return null;

  // Calculate qty × unitPrice and round to 2 decimal places (amount's precision)
  // Then compare with the stored amount. This ensures validation matches
  // what would actually be stored/displayed.
  const calculated = roundToPrecision(qtyVal * priceVal, 2);
  const rounded = roundToPrecision(amountVal, 2);

  // Use tiny epsilon for floating-point comparison after rounding
  return Math.abs(rounded - calculated) < 0.0001;
}

export function LineItemsSection({
  lineItems,
  originalLineItems,
  precision,
  formatQuantity,
  formatUnitPrice,
  formatAmount,
  onLineItemEdit,
  onLineItemDelete,
  onLineItemAdd,
}: LineItemsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem('acitra_line_items_expanded');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  const toggleExpanded = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    try {
      localStorage.setItem('acitra_line_items_expanded', String(newState));
    } catch (e) {
      console.error('Failed to save persistence preference', e);
    }
  };

  // Render section even if empty to allow adding items (null means loading)
  if (!lineItems) return null;

  const lineItemsTotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  // Count validation errors
  const errorCount = lineItems.filter((item) => {
    const isValid = amountsMatch(item);
    return isValid === false; // Only count explicit failures, not null
  }).length;

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <button
        onClick={toggleExpanded}
        className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-medium text-gray-900">Line Items</h2>
          <span className="text-sm text-gray-500">
            ({lineItems.length} item{lineItems.length !== 1 ? 's' : ''})
          </span>
          {errorCount > 0 && (
            <span className="inline-flex items-center gap-1 text-sm text-red-600">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              {errorCount} error{errorCount !== 1 ? 's' : ''}
            </span>
          )}
          {!isExpanded && (
            <span className="text-sm font-medium text-gray-700">
              — {formatAmount(lineItemsTotal)}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10 px-2 py-3"></th>
                <th className="w-24 px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Code
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="w-20 px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qty
                </th>
                <th className="w-20 px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  UOM
                </th>
                <th className="w-28 px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit Price
                </th>
                <th className="w-28 px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="w-10 px-2 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lineItems.map((item, idx) => {
                const calculatedAmount = calculateLineItemAmount(item);
                const isValid = amountsMatch(item);
                const originalItem = originalLineItems ? originalLineItems[idx] : undefined;

                // Helper to check if a specific field changed
                const isChanged = (field: keyof InvoiceLineItem) => {
                  if (!originalItem) return false;
                  const val = item[field];
                  const origVal = originalItem[field];
                  // Loose equality for null vs undefined, or strict for values
                  if (val == null && origVal == null) return false;
                  return val !== origVal;
                };

                // Detect if it's a new item (not present in original array)
                // Note: This relies on index matching which works for appending.
                // If we insert in middle or delete from middle, this might be tricky,
                // but currently we only append to the end and deletion re-indexes.
                // A more robust way would be IDs, but line items might not have IDs yet.
                // For now, if originalItem is undefined, it's considered new.
                const isNewItem = !originalItem;

                // Helper to determine cell style
                const getCellStyle = (field: keyof InvoiceLineItem) => {
                  if (isNewItem) return '!text-edited-600'; // All fields in new item are "edited"
                  return isChanged(field) ? '!text-edited-600' : '';
                };

                return (
                  <tr key={idx} className="group hover:bg-gray-50">
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => onLineItemDelete(idx)}
                        className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete line item"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </td>
                    <td className="py-2 text-sm text-gray-500">
                      <EditableValue
                        value={item.itemCode || '-'}
                        onSave={async (value) => {
                          await onLineItemEdit(idx, 'itemCode', value);
                        }}
                        textAlign="left"
                        width="w-full"
                        className={getCellStyle('itemCode')}
                        originalValue={
                          originalItem?.itemCode !== undefined
                            ? originalItem.itemCode || '-'
                            : undefined
                        }
                        onReset={
                          originalItem?.itemCode !== undefined
                            ? async () => {
                                await onLineItemEdit(
                                  idx,
                                  'itemCode',
                                  originalItem.itemCode ?? null
                                );
                              }
                            : undefined
                        }
                      />
                    </td>
                    <td className="py-2 text-sm text-gray-900 max-w-0">
                      <EditableValue
                        value={item.description || '-'}
                        onSave={async (value) => {
                          await onLineItemEdit(idx, 'description', value);
                        }}
                        textAlign="left"
                        width="w-full"
                        className={getCellStyle('description')}
                        originalValue={
                          originalItem?.description !== undefined
                            ? originalItem.description || '-'
                            : undefined
                        }
                        onReset={
                          originalItem?.description !== undefined
                            ? async () => {
                                await onLineItemEdit(
                                  idx,
                                  'description',
                                  originalItem.description ?? null
                                );
                              }
                            : undefined
                        }
                      />
                    </td>
                    <td className="py-2 text-sm text-gray-500">
                      <EditableValue
                        value={formatQuantity(item.quantity)}
                        rawValue={item.quantity}
                        onSave={async (value) => {
                          if (value === null) return;
                          const parsed = parseFloat(value.replace(/,/g, ''));
                          if (!isNaN(parsed)) {
                            await onLineItemEdit(idx, 'quantity', parsed);
                          }
                        }}
                        inputMode="decimal"
                        textAlign="right"
                        width="w-full"
                        className={getCellStyle('quantity')}
                        originalValue={
                          originalItem?.quantity !== undefined
                            ? formatQuantity(originalItem.quantity)
                            : undefined
                        }
                        onReset={
                          originalItem?.quantity !== undefined
                            ? async () => {
                                await onLineItemEdit(idx, 'quantity', originalItem.quantity!);
                              }
                            : undefined
                        }
                        calculatedValue={
                          item.unitPrice != null && item.unitPrice > 0
                            ? calculateWithRoundtrip(item.amount ?? 0, item.unitPrice, precision)
                            : undefined
                        }
                        onCalculate={
                          item.unitPrice != null && item.unitPrice > 0
                            ? async () => {
                                await onLineItemEdit(
                                  idx,
                                  'quantity',
                                  calculateWithRoundtrip(
                                    item.amount ?? 0,
                                    item.unitPrice!,
                                    precision
                                  )
                                );
                              }
                            : undefined
                        }
                      />
                    </td>
                    <td className="py-2 text-sm text-gray-500">
                      <EditableValue
                        value={item.unitOfMeasure || '-'}
                        onSave={async (value) => {
                          await onLineItemEdit(idx, 'unitOfMeasure', value);
                        }}
                        textAlign="left"
                        width="w-full"
                        className={getCellStyle('unitOfMeasure')}
                        originalValue={
                          originalItem?.unitOfMeasure !== undefined
                            ? originalItem.unitOfMeasure || '-'
                            : undefined
                        }
                        onReset={
                          originalItem?.unitOfMeasure !== undefined
                            ? async () => {
                                await onLineItemEdit(
                                  idx,
                                  'unitOfMeasure',
                                  originalItem.unitOfMeasure ?? null
                                );
                              }
                            : undefined
                        }
                      />
                    </td>
                    <td className="py-2 text-sm text-gray-500">
                      <EditableValue
                        value={formatUnitPrice(item.unitPrice)}
                        rawValue={item.unitPrice}
                        onSave={async (value) => {
                          if (value === null) return;
                          const parsed = parseFloat(value.replace(/,/g, ''));
                          if (!isNaN(parsed)) {
                            await onLineItemEdit(idx, 'unitPrice', parsed);
                          }
                        }}
                        inputMode="decimal"
                        textAlign="right"
                        width="w-full"
                        className={getCellStyle('unitPrice')}
                        originalValue={
                          originalItem?.unitPrice !== undefined
                            ? formatUnitPrice(originalItem.unitPrice)
                            : undefined
                        }
                        onReset={
                          originalItem?.unitPrice !== undefined
                            ? async () => {
                                await onLineItemEdit(idx, 'unitPrice', originalItem.unitPrice!);
                              }
                            : undefined
                        }
                        calculatedValue={
                          item.quantity != null && item.quantity > 0
                            ? calculateWithRoundtrip(item.amount ?? 0, item.quantity, precision)
                            : undefined
                        }
                        onCalculate={
                          item.quantity != null && item.quantity > 0
                            ? async () => {
                                await onLineItemEdit(
                                  idx,
                                  'unitPrice',
                                  calculateWithRoundtrip(
                                    item.amount ?? 0,
                                    item.quantity!,
                                    precision
                                  )
                                );
                              }
                            : undefined
                        }
                      />
                    </td>
                    <td className="py-2 text-sm font-medium text-gray-900">
                      <EditableValue
                        value={formatAmount(item.amount)}
                        rawValue={item.amount}
                        onSave={async (value) => {
                          if (value === null) return;
                          const parsed = parseFloat(value.replace(/,/g, ''));
                          if (!isNaN(parsed)) {
                            await onLineItemEdit(idx, 'amount', parsed);
                          }
                        }}
                        inputMode="decimal"
                        textAlign="right"
                        width="w-full"
                        className={getCellStyle('amount')}
                        originalValue={
                          originalItem?.amount !== undefined
                            ? formatAmount(originalItem.amount)
                            : undefined
                        }
                        onReset={
                          originalItem?.amount !== undefined
                            ? async () => {
                                await onLineItemEdit(idx, 'amount', originalItem.amount!);
                              }
                            : undefined
                        }
                        calculatedValue={roundToPrecision(
                          (item.quantity ?? 0) * (item.unitPrice ?? 0),
                          2
                        )}
                        onCalculate={async () => {
                          await onLineItemEdit(
                            idx,
                            'amount',
                            roundToPrecision((item.quantity ?? 0) * (item.unitPrice ?? 0), 2)
                          );
                        }}
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <ValidationIndicator
                        isValid={isValid}
                        captured={item.amount}
                        calculated={calculatedAmount}
                        formatAmount={formatAmount}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onLineItemAdd}
              className="text-sm font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1 ml-9"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Line Item
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
